<template lang="pug">
.task-item(@click="progress.hasActive && handleClick()")
  .task-left
    .left
      .icon-new-block
        SafeImg(class-name="left-logo" :src="taskItem.taskImgUrl")
    .mid
      .title
        span {{getTitle}}{{progressText}}
      .progress
        .progress-right(v-if="taskItem.amount || currentStageAmount")
          img.progress-right-img(:src="coinIcon")
          .progress-right-text +{{ currentStageAmount }}
        //- 推荐系统角标+文案
        .coupon-badge(v-if="hasCouponBadge")
          .coupon-icon
            img(:src="taskItem.couponInfo.icon")
          .coupon-text {{ taskItem.couponInfo.prizeDesc }}
        .progress-left(v-else) {{ taskItem.additionalDesc }}
  .right
    .btn(
      v-if="progress.hasActive"
      :class="{'can-do': taskItem.taskStatus === 'RUNNING'}"
    ) {{ taskItem.btnName }}
    .btn.disabled(v-else) 明日再来
</template>

<script>
import { getTokenAndLocation } from '@/utils';
import { ParamsUtil } from '@/utils/params';
import eventBus from '@/const/eventBus';
import SafeImg from '@/components/safe-img';
import Toast from '@/components/toast';
import Loading from '@/components/loading';
import { TASK_STATUS_V2, TaskStatus } from '@/const/task';
import { acceptTask } from '../../common/task';
import {
  processAdParams,
  handleFinishTask,
  loadRewardResultPlatformAds,
} from '../../common/helper';
import { loadPlatformAds, showPlatformAds } from '../../common/bridge';
import { TIERED_VIDEO_AB } from '../../consts';
import SPM from '../../common/spm';
import { playContinuousVideo } from '../../common/continuous-video-engine';

function tieredVideoProgressAdapter(stageOffers) {
  if (!stageOffers || stageOffers.length === 0) {
    return {
      completed: 0,
      total: 0,
      hasActive: false,
      firstUnfinishedIndex: -1,
    };
  }
  const total = stageOffers.length;
  let completed = 0;
  let firstUnfinishedIndex = -1;
  for (let i = 0; i < total; i += 1) {
    if (stageOffers[i].offerStatus === 3) {
      completed += 1;
    } else if (firstUnfinishedIndex === -1) {
      firstUnfinishedIndex = i;
    }
  }
  const hasActive = completed < total;
  return {
    completed,
    total,
    hasActive,
    firstUnfinishedIndex,
  };
}

export default {
  name: 'TieredVideoTask',
  components: { SafeImg },
  props: {
    taskItem: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    doubleInformation: {
      type: Object,
      default: () => ({}),
    },
    activeBtnClass: {
      type: String,
      default: 'doing',
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    progress() {
      return tieredVideoProgressAdapter(this.taskItem.stageOffers);
    },
    progressText() {
      const { completed, total } = this.progress;
      return `（${completed}/${total}）`;
    },
    currentStageAmount() {
      const { firstUnfinishedIndex } = this.progress;
      const offers = this.taskItem.stageOffers;
      if (!offers || offers.length === 0) return 0;
      const idx = firstUnfinishedIndex !== -1 ? firstUnfinishedIndex : offers.length - 1;
      return offers[idx].totalAmount;
    },
    coinIcon() {
      return 'https://m.hellobike.com/fd018348_new_coin.png';
    },
    hasCouponBadge() {
      return !!(this.taskItem.couponInfo && this.taskItem.couponInfo.prizeDesc);
    },
    getTitle() {
      if (this.taskItem.taskStatus === 'RUNNING') return `${this.taskItem.guideDesc}${this.taskItem.guideAwardDesc}`;
      return this.taskItem.taskName;
    },
  },
  methods: {
    getButtonState(item) {
      if (item.shouldAccept) return '0';
      if (item.status === TaskStatus.DOING) return '1';
      if (item.status === TaskStatus.CAN_DONE) return '2';
      return '';
    },
    async handleClick() {
      if (!this.progress.hasActive) return;
      const buttonState = this.getButtonState(this.taskItem);
      SPM.clickButton({
        buttonName: 'platform_taskClick',
        businessInfo: {
          task_id: this.taskItem.taskCode,
          taskBonus: this.taskItem.amount || '',
          rankNum: this.index + 1,
          task_name: this.getTitle + this.progressText,
          buttonState,
          taskSequence: this.index,
          isSurpriseTask: this.taskItem.isHighlight ? '1' : '0',
          ladderIndex: String(this.progress.completed + 1),
        },
      });
      try {
        let currentTaskItem = this.taskItem;
        const shouldAccept = this.taskItem.shouldAccept
          || this.taskItem.taskStatus === TASK_STATUS_V2.INIT;

        if (shouldAccept) {
          const { token, adCode, cityCode } = await getTokenAndLocation();
          const { success, info } = await acceptTask(this.taskItem, {
            token,
            cityInfo: { adCode, cityCode },
          });
          if (!success) return;

          // 领取成功后同步状态，避免按钮仍显示"领任务"
          this.taskItem.shouldAccept = false;
          this.taskItem.taskStatus = TASK_STATUS_V2.RUNNING;
          if (info && info.btnName) {
            this.taskItem.btnName = info.btnName;
          }
          currentTaskItem = {
            ...this.taskItem,
            ...(info || {}),
          };
        }

        // 命中对照组 → 走老版单视频流程（与 playContinuousVideo 互斥）
        if (currentTaskItem.incentiveVideoTaskStyle === TIERED_VIDEO_AB.oldVersion) {
          await this.playLegacyTieredVideo(currentTaskItem);
          this.$emit('completed');
          return;
        }

        const { firstUnfinishedIndex } = tieredVideoProgressAdapter(currentTaskItem.stageOffers);
        if (firstUnfinishedIndex === -1) return;

        const firstStage = currentTaskItem.stageOffers[firstUnfinishedIndex];
        const hasBanner = currentTaskItem.incentiveVideoTaskStyle
          === TIERED_VIDEO_AB.newVersionWithBanner;

        const result = await playContinuousVideo({
          firstVideoParams: {
            adUnitId: currentTaskItem.advertisementUnitId,
            taskCode: currentTaskItem.taskCode,
            rewardCount: firstStage.totalAmount,
          },
          getNextVideoParams: ({ currentIndex }) => {
            const offers = currentTaskItem.stageOffers;
            // currentIndex 是引擎内部的相对索引（第几个视频），需要转换为 stageOffers 的绝对索引
            // 从 firstUnfinishedIndex 开始，跳过已完成项，找到第 currentIndex 个未完成项的下一个
            let count = 0;
            let lastFoundIdx = -1;
            for (let i = 0; i < offers.length; i += 1) {
              if (offers[i].offerStatus !== 3) {
                if (count === currentIndex) {
                  lastFoundIdx = i;
                  break;
                }
                count += 1;
              }
            }
            // 从当前项的下一个开始找下一个未完成项
            for (let i = lastFoundIdx + 1; i < offers.length; i += 1) {
              if (offers[i].offerStatus !== 3) {
                return {
                  adUnitId: currentTaskItem.advertisementUnitId,
                  taskCode: currentTaskItem.taskCode,
                  rewardCount: offers[i].totalAmount,
                };
              }
            }
            return null;
          },
          bannerAdsInfo: hasBanner ? processAdParams(this.doubleInformation || {}) : {},
          toastConfig: {
            none_completed_failed: '视频加载失败，请稍后重试',
            none_completed_closed: '任务未完成',
            mid_video_failed: '视频加载失败，请稍后重试',
            mid_video_closed: '任务未完成',
          },
          callbacks: {
            onPopupExpose: ({ bannerAdsInfo: bInfo }) => {
              SPM.moduleExpose({
                moduleId: 'Welfare_jilishipinpop',
                contentId: 'Welfare_jilishipinpop',
                businessInfo: {
                  adverTestVersion: currentTaskItem.incentiveVideoTaskStyle,
                  adv: (bInfo && bInfo.adUnitId) ? '0' : '1',
                },
              });
            },
            onAction: ({ action }) => {
              const buttonMap = {
                nextVideo: 'Welfare_jilishipinpop_next',
                bannerReward: 'Welfare_jilishipinpop_adv',
                videoBannerReward: 'Welfare_jilishipinpop_adv',
                close: 'Welfare_jilishipinpop_close',
              };
              if (buttonMap[action]) {
                SPM.clickButton({
                  buttonName: buttonMap[action],
                  businessInfo: {
                    adverTestVersion: currentTaskItem.incentiveVideoTaskStyle,
                  },
                });
              }
            },
          },
          finishTaskInfo: this.doubleInformation.bannerTaskRelationCouponInfo,
        });

        if (result.adLoadFailed) {
          this.$emit('adLoadFailed', this.taskItem);
          return;
        }
        if (!result.success) return;
        this.$emit('completed');
      } catch (error) {
        Loading.close();
        Toast.show('网络数据不稳定，请稍后重试');
      }
    },
    // 老版单视频流程（命中对照组时使用）
    // 流程：定位首个未完成阶段 → 视频加载/播放 → 推进任务 → 领奖弹窗 → 处理 4 种 action
    // nextVideo / 翻倍后 nextVideo 时本地标记 offerStatus=3 推进指针，递归 playOnce
    async playLegacyTieredVideo(taskItem) {
      const { advertisementUnitId: adUnitId, taskCode } = taskItem;
      const bannerInfo = this.doubleInformation || {};
      const { bannerTaskRelationCouponInfo } = bannerInfo;

      const playOnce = async () => {
        const { firstUnfinishedIndex } = tieredVideoProgressAdapter(
          taskItem.stageOffers,
        );
        if (firstUnfinishedIndex === -1) return;

        const stage = taskItem.stageOffers[firstUnfinishedIndex];
        const isEnd = firstUnfinishedIndex === taskItem.stageOffers.length - 1;
        const nextStage = taskItem.stageOffers[firstUnfinishedIndex + 1];

        // 1. 加载视频
        Loading.show();
        const loadRes = await loadPlatformAds({
          pageName: 'rewardVideo',
          timeout: '5',
          scene: 'rebateBonus',
          adsource: ParamsUtil.getParams().adsource || '',
          adsInfo: {
            adUnitId,
            rewardCount: stage.totalAmount,
            taskCode,
            business_line: 'mars',
          },
        });
        if (!loadRes || !loadRes.pageId) {
          Loading.close();
          Toast.show('视频加载失败，请稍后重试');
          this.$emit('adLoadFailed', taskItem);
          return;
        }
        Loading.close();

        // 2. 播放视频
        const showRes = await showPlatformAds({ pageId: loadRes.pageId });
        if (showRes && showRes.errCode) {
          Toast.show('任务未完成');
          return;
        }
        const { inspireType } = showRes || {};

        // 3. 推进任务完成
        const finishRes = await handleFinishTask({
          inspireType,
          bannerTaskRelationCouponInfo,
        });
        if (!finishRes || !finishRes.success) return;

        // 4. 加载领奖结果弹窗 + 触发金币动画
        const popupRes = await this.loadRewardPopup({
          taskItem,
          stageIndex: firstUnfinishedIndex,
          bannerInfo,
        });
        if (!popupRes) return;
        const { action, inspireType: popupInspireType, adsInfo: popupAdsInfo } = popupRes;
        const rewardTotalCount = (popupAdsInfo && popupAdsInfo.rewardTotalCount) || '';

        // 弹窗按钮点击埋点
        const buttonMap = {
          nextVideo: 'Welfare_jilishipinpop_next',
          bannerReward: 'Welfare_jilishipinpop_adv',
          videoBannerReward: 'Welfare_jilishipinpop_adv',
          close: 'Welfare_jilishipinpop_close',
        };
        if (buttonMap[action]) {
          SPM.clickButton({
            buttonName: buttonMap[action],
            businessInfo: {
              adverTestVersion: taskItem.incentiveVideoTaskStyle,
            },
          });
        }

        // 5. 处理用户行为
        if (action === 'nextVideo') {
          // 本地标记当前阶段已完成，推进未完成项指针，递归继续播放
          // eslint-disable-next-line no-param-reassign
          taskItem.stageOffers[firstUnfinishedIndex].offerStatus = 3;
          await playOnce();
        } else if (action === 'bannerReward' || action === 'videoBannerReward') {
          const nextAction = await this.handleBannerReward({
            inspireType: popupInspireType,
            bannerTaskRelationCouponInfo,
            isEnd,
            rewardTotalCount,
            nextStage,
          });
          if (nextAction === 'nextVideo') {
            // eslint-disable-next-line no-param-reassign
            taskItem.stageOffers[firstUnfinishedIndex].offerStatus = 3;
            await playOnce();
          }
        }
        // close：直接结束（已在埋点中上报）
      };

      try {
        await playOnce();
      } catch (e) {
        Loading.close();
        Toast.show('网络数据不稳定，请稍后重试');
      }
    },
    // 加载并展示老版领奖结果弹窗（命中对照组时使用）
    // 流程：loadPlatformAds(rewardResult) → 触发金币动画 → showPlatformAds → 返回用户 action
    async loadRewardPopup({ taskItem, stageIndex, bannerInfo }) {
      const offers = taskItem.stageOffers || [];
      const isEnd = stageIndex === offers.length - 1;
      const curStage = offers[stageIndex] || {};
      const nextStage = offers[stageIndex + 1] || {};

      Loading.show();
      const adsInfo = processAdParams(bannerInfo || {});
      const params = {
        pageName: 'rewardResult',
        timeout: '2',
        scene: 'rebateBonus',
        adsource: ParamsUtil.getParams().adsource || '',
        adsInfo,
        otherInfo: {
          data: {
            isEnd,
            rewardCount: curStage.totalAmount || '',
            nextRewardCount: nextStage.totalAmount || '',
          },
        },
      };
      const loadRes = await loadPlatformAds(params);
      Loading.close();
      if (!loadRes || !loadRes.pageId) return null;

      // 触发金币聚拢动画（与红包老版一致）
      eventBus.$emit('playCollectAnimate');

      // 弹窗曝光埋点
      SPM.moduleExpose({
        moduleId: 'Welfare_jilishipinpop',
        contentId: 'Welfare_jilishipinpop',
        businessInfo: {
          adverTestVersion: taskItem.incentiveVideoTaskStyle,
          adv: (adsInfo.adUnitId && adsInfo.rewardCount) ? '0' : '1',
          ladderIndex: String(stageIndex + 1),
        },
      });

      const showRes = await showPlatformAds({ pageId: loadRes.pageId });
      return showRes || null;
    },
    // 处理翻倍 banner 奖励（命中对照组时使用）
    // 内部走 loadRewardResultPlatformAds：再次推进任务 + 加载结果弹窗，返回用户 action
    async handleBannerReward({
      inspireType, bannerTaskRelationCouponInfo, isEnd, rewardTotalCount, nextStage,
    }) {
      const result = await loadRewardResultPlatformAds({
        inspireType,
        bannerTaskRelationCouponInfo,
        otherInfo: {
          data: {
            isEnd,
            rewardCount: rewardTotalCount,
            nextRewardCount: (nextStage && nextStage.totalAmount) || '',
            isTotalReward: true,
          },
        },
      });
      return (result && result.action) || null;
    },
  },
};
</script>

<style lang="less" scoped>
.task-item {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  align-items: center;
  position: relative;
  padding: 0.12rem;

  .task-left {
    display: flex;

    .left {
      margin-right: 0.1rem;

      .icon-new-block {
        overflow: hidden;
        width: 0.4rem;
        height: 0.4rem;
        border-radius: 0.12rem;
      }
    }
  }

  .mid {
    display: flex;
    justify-content: center;
    flex-direction: column;
    flex: 1;
    max-width: 1.9rem;
    overflow: hidden;
    align-items: flex-start;

    .title {
      max-width: 1.9rem;
      font-size: 0.14rem;
      line-height: 0.2rem;
      font-weight: 500;
      color: #242729;
      margin-bottom: 0.02rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .progress {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: 100%;

      .progress-left {
        flex: 1;
        min-width: 0;
        color: rgba(90, 96, 102, 0.8);
        font-size: 0.12rem;
        font-family: PingFangSC-Regular, PingFang SC, sans-serif;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .progress-right {
        display: flex;
        align-items: center;
        height: 0.16rem;
        padding-right: 0.08rem;
        border-radius: 0.16rem;
      }

      .progress-right-img {
        width: 0.16rem;
        height: 0.16rem;
      }

      .progress-right-text {
        color: #fe852a;
        font-weight: 600;
        font-size: 0.12rem;
        margin-left: 0.02rem;
      }

      .coupon-badge {
        display: flex;
        align-items: center;
        height: 0.18rem;
        border-radius: 0.06rem;
        overflow: hidden;
        border: 0.01rem solid #ffe3e0;
        background-color: #fff;
        width: fit-content;
        flex-shrink: 0;

        .coupon-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 0.19rem;
          height: 0.18rem;
          background-color: #ffe3e0;
          flex-shrink: 0;

          img {
            width: 0.15rem;
            height: 0.15rem;
          }
        }

        .coupon-text {
          padding: 0 0.04rem;
          font-size: 0.11rem;
          font-weight: 500;
          color: #ff5729;
          line-height: 0.16rem;
          white-space: nowrap;
        }
      }
    }
  }

  .right {
    flex-direction: column;
    align-items: flex-end;
    position: relative;

    .btn {
      background-color: #ffe888;
      color: #000;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 0.62rem;
      height: 0.28rem;
      line-height: normal;
      font-size: 0.12rem;
      font-weight: 500;
      border-radius: 0.32rem;

      &.disabled {
        background: #f0f3f5 !important;
        color: #bcc4cc !important;
      }
    }

    .can-do {
      background: #fff;
      color: #fb750f;
      border: 0.01rem solid #ff8000;
    }
  }
}
</style>
